import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import { OAUTH_PROVIDER_LABELS } from '@featit/shared/types/index';
import { useStores } from '@/hooks/useStores';
import { ContinueInBrowserNotice } from '@/desktop/ContinueInBrowserNotice';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/** The query parameters a provider round trip comes back with; any of them means it is over. */
const RETURN_PARAMS = ['oauthCode', 'oauthError', 'oauthLinked'] as const;

/**
 * The desktop app's "finish this in your browser" note for a provider sign-in or link (ADR-160
 * AC-18, I-1078), shown while `authStore.oauthInBrowser` names the provider. It goes away when the
 * `featit-return` link brings the outcome back to this screen, or when the person dismisses it to
 * try another way.
 */
export const OAuthContinueInBrowserNotice = observer(function OAuthContinueInBrowserNotice({
  purpose,
}: {
  purpose: 'sign-in' | 'link';
}) {
  const t = useT();
  const { authStore } = useStores();
  const [params] = useSearchParams();
  const returned = RETURN_PARAMS.some((name) => params.has(name));

  useEffect(() => {
    if (returned && authStore.oauthInBrowser) authStore.dismissOAuthInBrowser();
  }, [authStore, returned]);

  const provider = authStore.oauthInBrowser;
  if (!provider || returned) return null;
  const label = OAUTH_PROVIDER_LABELS[provider];

  return (
    <ContinueInBrowserNotice
      testId="oauth-continue-in-browser"
      title={t('banners.oauth.continueInBrowser')}
      detail={
        purpose === 'sign-in'
          ? t('banners.oauth.finishSignIn', { provider: label })
          : t('banners.oauth.finishConnect', { provider: label })
      }
      dismissLabel={purpose === 'sign-in' ? t('banners.oauth.useAnotherWay') : t('banners.dismiss')}
      onDismiss={() => authStore.dismissOAuthInBrowser()}
    />
  );
});
